import React from 'react';
import { getRandomId } from './helpers';
import { Redirect, Route } from 'react-router-dom';
import { modal } from '../services/ModalService';
import { language } from '../services/LanguageService';
import { ModalBodyAppInfo } from '../components/ModalBodyAppInfo';
import { catchError, map, of, Subject } from 'rxjs';
import { ModalBodyConfirm } from '../components/ModalBodyConfirm';
import { InlineNotification } from '../components/Carbon';
import { getRunningHttpGatewayTransfers } from './transfer';
import { ModalHttpTransfersRunning } from '../components/ModalHttpTransfersRunning';
import { ModalDownloadTable } from '../components/ModalDownloadTable';
import { ModalFormRender } from '../components/ModalFormRender';
import { notification } from '../services/NotificationService';
/**
 * Takes the route structure file and renders a list of React Router DOM Components for that setup
 * If redirect is used exact will always be true
 *
 * @param allRoutes - array of routes that represent an app's route structure
 *
 * @returns an array of components to load in a Switch statement.
 */
export const getRecursiveRoutes = (allRoutes) => {
    const routerComponents = [];
    allRoutes.filter(route => !route.hidden).forEach(route => {
        const checkRoute = (checkedRoute) => {
            const routeToCheck = Object.assign({}, checkedRoute);
            if (routeToCheck.children) {
                routeToCheck.children.filter(childRoute => !childRoute.hidden).forEach(childRoute => {
                    const childRouteToCheck = Object.assign({}, childRoute);
                    childRouteToCheck.path = routeToCheck.path + childRouteToCheck.path;
                    checkRoute(childRouteToCheck);
                });
            }
            else {
                const random = getRandomId();
                routerComponents.push(routeToCheck.redirect ?
                    React.createElement(Redirect, { exact: true, key: random, from: routeToCheck.path, to: routeToCheck.redirect }) :
                    React.createElement(Route, { key: random, path: routeToCheck.path, exact: routeToCheck.exact, component: routeToCheck.component }));
            }
        };
        checkRoute(route);
    });
    return routerComponents;
};
/**
 * Open the application information modal
 *
 * @param subManager - The subscription manager to link the modal too
 */
export const openAppInfoModal = (subManager, expandErrors) => {
    const modalData = modal.prepare();
    const modalProps = {
        modalHeading: language.get('modalAppInfo.heading'),
        modalLabel: language.get('modalAppInfo.subHeading'),
        size: 'lg',
        passiveModal: true,
    };
    subManager.setSubscription(modal.create(modalData.id, modalProps, React.createElement(ModalBodyAppInfo, { expandErrors: expandErrors, modalClose: modalData.callback })).subscribe({ next: () => { }, error: () => { } }));
};
/**
 * Show the user decrypt info
 *
 * @returns an observable indicating if user wants to decrypt
 */
export const promptForDecrypt = () => {
    const modalData = modal.prepare();
    const modalProps = {
        modalHeading: language.get('modalDecrypt.decryptItems'),
        primaryButtonText: language.get('modalDecrypt.decrypt'),
        secondaryButtonText: language.get('modalDecrypt.skip'),
    };
    const disableSubject = new Subject();
    const formModel = {
        passphrase: '',
    };
    const modalBodyProps = {
        modalClose: modalData.callback,
        formData: {
            id: 'modal-decrypt-files',
            model: formModel,
            items: [
                {
                    type: 'sub-header',
                    label: language.get('modalDecrypt.decryptItemsDescription'),
                    helperText: language.get('modalDecrypt.decryptItemsWarning'),
                },
                {
                    type: 'password',
                    required: true,
                    pointer: 'passphrase',
                    label: language.get('modalDecrypt.passphrase'),
                },
            ],
        },
        disableSubject: disableSubject,
    };
    return modal.create(modalData.id, modalProps, React.createElement(ModalFormRender, Object.assign({}, modalBodyProps)), disableSubject.asObservable()).pipe(map(() => {
        notification.create('info', language.get('modalDecrypt.passphraseAccepted'));
        return { passphrase: formModel.passphrase };
    }), catchError(() => {
        return of({ passphrase: '' });
    }));
};
/**
 * Prompt the user to see if they want to reload the app
 *
 * @param subManager - The subscription manager to link the modal too
 */
export const promptForReload = (subManager) => {
    const modalInfo = modal.prepare();
    const httpActiveTransfers = !!getRunningHttpGatewayTransfers().length;
    const modalBodyProps = {
        modalClose: modalInfo.callback,
        description: language.get('refreshPageModal.needToReload'),
        warningDescription: httpActiveTransfers ? React.createElement(InlineNotification, { kind: "warning", hideCloseButton: true, lowContrast: true, title: language.get('refreshPageModal.caution'), subtitle: language.get('refreshPageModal.needToReloadHelper') }) : undefined,
    };
    const modalProps = {
        modalHeading: language.get('refreshPageModal.reloadApplication'),
        primaryButtonText: language.get('refreshPageModal.reload'),
        secondaryButtonText: language.get('refreshPageModal.close'),
    };
    subManager.setSubscription(modal.create(modalInfo.id, modalProps, React.createElement(ModalBodyConfirm, Object.assign({}, modalBodyProps))).subscribe({
        next: () => {
            window.location.reload();
        },
        error: () => { },
    }));
};
/**
 * Prompt the user to see if they want to reload the app
 *
 * @param subManager - The subscription manager to link the modal too
 * @param redirectUrl - the URL to open when transfers finish
 */
export const warnForRunningGateway = (subManager, redirectUrl) => {
    const modalInfo = modal.prepare();
    const modalProps = {
        modalHeading: language.get('httpWarningModal.title'),
        passiveModal: true,
        className: 'aspera-modal-http-gateway-catch',
        size: 'lg',
    };
    subManager.setSubscription(modal.create(modalInfo.id, modalProps, React.createElement(ModalHttpTransfersRunning, { redirectUrl: redirectUrl, modalClose: modalInfo.callback })).subscribe({
        next: () => {
            // If for some reason it gets closed reopen.  Only way to kill is reload.
            warnForRunningGateway(subManager, redirectUrl);
        },
        error: () => {
            // If for some reason it gets closed reopen.  Only way to kill is reload.
            warnForRunningGateway(subManager, redirectUrl);
        },
    }));
};
/**
 * Open a modal for downloading a table as a CSV
 *
 * @param tableData - The table data to use for passing to Modal
 * @param subManager - The sub manager for the view using it
 */
export const downloadTableAsCsv = (tableData, subManager, rawData) => {
    const modalInfo = modal.prepare();
    const modalProps = {
        modalHeading: language.get('downloadAsCsv.title'),
        passiveModal: true,
        size: 'sm',
    };
    subManager.setSubscription(modal.create(modalInfo.id, modalProps, React.createElement(ModalDownloadTable, { tableData: tableData, modalClose: modalInfo.callback, rawData: rawData })).subscribe({
        next: () => { },
        error: () => { },
    }));
};
/**
 * Modal for asking if user is sure about leaving a dirty form
 *
 * @param subManager - The subscription manager to link the modal too
 * @param callback - The callback when they choose to leave
 */
export const dirtyFormConfirmation = (callback) => {
    const modalData = modal.prepare();
    const modalProps = {
        modalHeading: language.get('forms.unsavedChangesHeading'),
        size: 'sm',
        secondaryButtonText: language.get('forms.leave'),
        primaryButtonText: language.get('forms.stayOnThisPage'),
    };
    // eslint-disable-next-line
    modal.create(modalData.id, modalProps, React.createElement(ModalBodyConfirm, { description: language.get('forms.unsavedChangesDescription'), modalClose: modalData.callback })).subscribe({ next: () => { }, error: () => callback() });
};
const exports = {
    getRecursiveRoutes,
    openAppInfoModal,
    promptForReload,
    warnForRunningGateway,
    downloadTableAsCsv,
};
export default exports;
